/**
 * Parent portal — a warm, plain-language, reassurance-first lens on ONE child.
 * The job: "how are they doing, and how do I help?" Same roster the teacher reads
 * (roleProjections.ts), re-voiced with no jargon. The learner is referred to with
 * they/them: the roster stores no gender (minor-safe, first name + roll only).
 * Progress is framed against their own past and the syllabus, never against peers.
 *
 * Interactive: term/topic cards drill into a per-topic detail (back nav), the
 * WhatsApp digest subscribes, and "Replay the lesson" opens the real interactive
 * video (/learn/:shareToken).
 */

import { useState } from 'react';
import {
  ArrowLeft, Calendar, Bell, Clock, Award, CheckCircle2, AlertTriangle, BookOpen, Target,
  Eye, Send, Home, Compass, Heart, Repeat, TrendingUp, ArrowRight, Flame, ChevronRight, Play,
} from 'lucide-react';
import type { ClassRoster, Learner } from './mockData';
import { CONCEPTS, TOPICS, attemptOf, conceptLabel, conceptsOfTopic, conceptMasteryOf } from './mockData';
import { Donut } from './charts';
import { TrendLine, CoverageBar } from './portalCharts';
import { useOpenLesson, useLocalFlag } from './portalHooks';
import {
  understandingBand, topicBandLabel, masteredConcepts, flaggedConcepts, focusConcepts, developingConcepts,
  masteryTrend, weeklyActivity, weeklyDigest, syllabusPace, atHomeTips, lastAttempt, attemptsByDate, topStrengthArea,
  plainMisconception,
} from './roleProjections';

const TONE = {
  good: { c: '#2F8F80', ink: '#1F6B5F', soft: 'rgba(47,143,128,0.14)' },
  warn: { c: '#C99A3B', ink: '#9A7320', soft: 'rgba(201,154,59,0.16)' },
  bad: { c: '#CB5B6B', ink: '#B23B4E', soft: 'rgba(203,91,107,0.15)' },
} as const;
const daysAgo = (n: number) => (n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`);
const TOPIC_PLAIN: Record<string, string> = {
  electrostatics: 'how electric charges push and pull, and the invisible fields around them.',
  'current-electricity': 'how electricity flows through a circuit, and what speeds it up or slows it down.',
  'ray-optics': 'how light bends, reflects and focuses through mirrors and lenses.',
  'em-waves': 'the waves, from radio to X-rays, that carry energy through empty space.',
};

export function ParentPortal({ roster, learner, onBack }: { roster: ClassRoster; learner: Learner; onBack?: () => void }) {
  const [topicId, setTopicId] = useState<string | null>(null);
  const openLesson = useOpenLesson();
  const [subscribed, toggleSub] = useLocalFlag(`learn-whatsapp-${learner.id}`);

  if (topicId) {
    return <ParentTopicDetail roster={roster} learner={learner} topicId={topicId} onBack={() => setTopicId(null)} openLesson={openLesson} />;
  }

  const band = understandingBand(learner.overallMasteryPercent);
  const bandTone = TONE[band.tone];
  const mastered = masteredConcepts(learner);
  const flagged = flaggedConcepts(learner);
  const act = weeklyActivity(learner);
  const digest = weeklyDigest(learner);
  const syl = syllabusPace(learner);
  const tips = atHomeTips(learner);
  const trend = masteryTrend(learner);
  const last = lastAttempt(learner);
  const lastTopic = last ? TOPICS.find((t) => t.id === last.topicId) : undefined;
  const nextTopic = TOPICS.find((t) => !attemptOf(learner, t.id));
  const [nextLabel, nextDesc] = nextTopic ? [nextTopic.title, TOPIC_PLAIN[nextTopic.id] ?? ''] : ['Wave Optics', 'interference and diffraction of light.'];
  const avgWatch = learner.attempts.length ? Math.round(learner.attempts.reduce((s, a) => s + a.watchPercent, 0) / learner.attempts.length) : 0;
  const trendFirst = trend[0]?.v ?? 0;
  const trendLast = trend[trend.length - 1]?.v ?? 0;

  const kpis = [
    { icon: BookOpen, lbl: 'Topics covered', val: `${learner.attempts.length} of ${TOPICS.length}`, sub: `${CONCEPTS.length} concepts across the term` },
    { icon: Target, lbl: 'Understanding', val: band.label, sub: `${learner.overallMasteryPercent}% of concepts secure`, ink: bandTone.ink },
    { icon: Clock, lbl: 'Time learning', val: `${act.minutes} min`, sub: `across ${act.days} days this week` },
    { icon: Eye, lbl: 'Lesson habit', val: avgWatch >= 85 ? 'Finishes them' : avgWatch >= 60 ? 'Most of each' : 'Needs a nudge', sub: `watches ~${avgWatch}% of each lesson` },
  ];

  return (
    <div className="pt-2">
      {onBack && (
        <button onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]">
          <ArrowLeft className="h-4 w-4" /> Back to class
        </button>
      )}

      <div className="mb-4 flex items-center gap-2.5 text-[14px] text-[#56505F] dark:text-[#B2B0C0]">
        <BookOpen className="h-4 w-4 text-[#6D28D9] dark:text-[#B69BF5]" />
        <span><b className="font-semibold text-[#1B1826] dark:text-white">Progress report for {learner.firstName}</b> · in plain language, from their actual work in each lesson. Updated after every test.</span>
      </div>

      {/* STATUS HERO */}
      <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <div className="flex flex-wrap items-center gap-6">
          <Donut percent={learner.overallMasteryPercent} color={bandTone.c} size={118} caption="on track" />
          <div className="min-w-[240px] flex-1">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#8A8593]">{learner.firstName} · {roster.name} · Physics · {roster.schoolName}</div>
            <div className="mt-2 font-serif text-[24px] font-medium leading-[1.34] tracking-tight">
              {learner.firstName} is {band.tone === 'good' ? 'doing well and keeping up with' : band.tone === 'warn' ? 'keeping pace with' : 'finding parts of'} the Class XII syllabus.
            </div>
            <div className="mt-3.5 flex flex-wrap gap-2">
              <Chip soft={bandTone.soft} ink={bandTone.ink}><CheckCircle2 className="h-3 w-3" />Understanding: {band.label}</Chip>
              <PlainPill><Clock className="h-3 w-3" />Studied {daysAgo(learner.lastActiveDaysAgo)}</PlainPill>
              <PlainPill><Award className="h-3 w-3" />{mastered.length} of {CONCEPTS.length} concepts secure</PlainPill>
            </div>
          </div>
        </div>
      </div>

      {/* THIS WEEK DIGEST (parent-only cadence) */}
      <div className="mt-4 rounded-2xl border bg-white p-6 dark:bg-white/[0.04]" style={{ borderColor: 'color-mix(in srgb, #6D28D9 30%, transparent)' }}>
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#6D28D9] dark:text-[#B69BF5]"><Calendar className="h-3.5 w-3.5" />This week · a 30-second read</div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-black/[0.03] px-2.5 py-1 text-[11px] font-semibold text-[#6D28D9] dark:border-white/10 dark:bg-white/[0.06] dark:text-[#B69BF5]"><Bell className="h-3 w-3" />Weekly digest</span>
        </div>
        <div className="mt-2.5 font-serif text-[20px] font-medium tracking-tight">
          {learner.firstName} studied {digest.days} days{digest.finished ? `, finished ${digest.finished}` : ''}, and secured {digest.newSecure} new concepts.
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <PlainPill><Clock className="h-3 w-3" />{digest.days} days · {digest.minutes} min</PlainPill>
          {digest.finished && <Chip soft={TONE.good.soft} ink={TONE.good.ink}><CheckCircle2 className="h-3 w-3" />{digest.finished} finished</Chip>}
          <Chip soft={TONE.good.soft} ink={TONE.good.ink}><Award className="h-3 w-3" />+{digest.newSecure} concepts secured</Chip>
          {digest.reinforce && <Chip soft={TONE.warn.soft} ink={TONE.warn.ink}><AlertTriangle className="h-3 w-3" />1 thing to reinforce</Chip>}
        </div>
        <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3 border-t border-black/10 pt-3.5 dark:border-white/10">
          <div className="text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{digest.reinforce ? <><b className="text-[#1B1826] dark:text-white">Your one action this week:</b> a 2-minute chat about {digest.reinforce}, the steps are below.</> : <><b className="text-[#1B1826] dark:text-white">Nothing needed this week.</b> {learner.firstName} is on top of it, a word of encouragement goes a long way.</>}</div>
          <button onClick={toggleSub}
            className={`inline-flex items-center gap-1.5 rounded-[10px] border px-3.5 py-2 text-[12.5px] font-semibold transition-colors ${subscribed ? 'border-transparent bg-[#2F8F80] text-white' : 'text-[#1F6B5F] hover:bg-[#2F8F80]/[0.06] dark:text-[#5FC7B6]'}`}
            style={subscribed ? undefined : { borderColor: 'color-mix(in srgb, #2F8F80 40%, transparent)' }}>
            {subscribed ? <><CheckCircle2 className="h-3.5 w-3.5" />Subscribed · every Sunday</> : <><Send className="h-3.5 w-3.5" />Get this every Sunday on WhatsApp</>}
          </button>
        </div>
      </div>

      {/* SYLLABUS PACE (vs curriculum norm, never peers) */}
      <div className="mt-4 flex flex-wrap items-center gap-6 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <div className="min-w-[240px] flex-1">
          <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]"><BookOpen className="h-3.5 w-3.5" />On pace with the syllabus</div>
          <p className="mt-2 text-[14px] text-[#56505F] dark:text-[#B2B0C0]">
            <b className="text-[#1B1826] dark:text-white">{syl.done} of {syl.total} chapters</b> of {syl.term} covered so far, right on schedule for this point in the term. Measured against the course plan, never against other children.
          </p>
        </div>
        <div className="min-w-[240px] flex-1"><CoverageBar done={syl.done} total={syl.total} expectedByNow={syl.expectedByNow} /></div>
      </div>

      {/* AT A GLANCE */}
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.lbl} className="rounded-2xl border border-black/10 bg-white p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <div className="flex items-center gap-2 text-[#8A8593]">
                <Icon className="h-[18px] w-[18px] text-[#6D28D9] dark:text-[#B69BF5]" />
                <span className="text-[11.5px] font-semibold uppercase tracking-wide">{k.lbl}</span>
              </div>
              <div className="mt-3 font-serif text-[29px] font-medium tracking-tight" style={k.ink ? { color: k.ink } : undefined}>{k.val}</div>
              <div className="mt-1 text-[12px] text-[#8A8593]">{k.sub}</div>
            </div>
          );
        })}
      </div>

      {/* STRENGTHS/HELP + WHAT THEY'RE WORKING ON */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.25fr_1fr]">
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <h3 className="font-serif text-[16px] font-semibold">Strengths, and where they need a little help</h3>
          <p className="mt-0.5 text-[12.5px] text-[#8A8593]">The same picture their teacher sees, without the jargon.</p>
          <div className="mt-4 flex flex-col gap-3">
            {mastered.length > 0 && (
              <ParentInsight tone="good" title={`Really strong on ${topStrengthArea(learner)}`}
                body={`${learner.firstName} answers these quickly and consistently, a sign the ideas are secure, not memorised.`} />
            )}
            {flagged.map((c) => (
              <button key={c.conceptTag} onClick={() => setTopicId(CONCEPTS.find((x) => x.tag === c.conceptTag)?.topicId ?? null)}
                className="flex gap-3 rounded-xl border border-black/10 bg-black/[0.015] p-[14px] text-left transition-colors hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-white/[0.05]">
                <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] bg-[#CB5B6B]/16 text-[#B23B4E]"><AlertTriangle className="h-[18px] w-[18px]" /></span>
                <div className="flex-1">
                  <h5 className="mb-0.5 flex items-center gap-1 text-[13px] font-bold">{c.state === 'emerging' ? 'The trickiest bit right now' : 'Nearly there'}: {conceptLabel(c.conceptTag)}<ChevronRight className="h-3.5 w-3.5 text-[#8A8593]" /></h5>
                  <p className="text-[12.5px] text-[#56505F] dark:text-[#B2B0C0]">{c.lastMisconceptionTag ? plainParentOf(c.lastMisconceptionTag) : ''}</p>
                </div>
              </button>
            ))}
            {flagged.length === 0 && developingConcepts(learner).length === 0 && focusConcepts(learner).length === 0 && (
              <p className="text-[13px] text-[#56505F] dark:text-[#B2B0C0]">No open gaps right now. {learner.firstName} is on top of every concept covered so far.</p>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <h3 className="font-serif text-[16px] font-semibold">What they&rsquo;re working on now</h3>
          <p className="mt-0.5 text-[12.5px] text-[#8A8593]">Most recent topic, in everyday terms.</p>
          {lastTopic && last && (
            <button onClick={() => setTopicId(lastTopic.id)}
              className="mt-4 block w-full rounded-xl border border-black/10 bg-black/[0.015] p-4 text-left transition-colors hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-white/[0.05]">
              <div className="flex items-center justify-between gap-2">
                <b className="flex items-center gap-1 font-serif text-[17px]">{lastTopic.title}<ChevronRight className="h-4 w-4 text-[#8A8593]" /></b>
                <Chip soft={TONE[understandingBand(last.scorePercent).tone].soft} ink={TONE[understandingBand(last.scorePercent).tone].ink}>{topicBandLabel(last.scorePercent)}</Chip>
              </div>
              <p className="mt-2 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{TOPIC_PLAIN[lastTopic.id] ?? ''} {last.replays > 0 ? `It is a step up in difficulty, and they are putting the effort in, they replayed this lesson ${last.replays} times.` : ''}</p>
            </button>
          )}
          <button onClick={() => (nextTopic ? openLesson(nextTopic.id) : openLesson(TOPICS[TOPICS.length - 1].id))}
            className="mt-4 flex w-full items-center gap-2.5 rounded-xl border border-dashed border-black/15 p-3.5 text-left transition-colors hover:border-[#7C3AED]/40 dark:border-white/15">
            <ArrowRight className="h-4 w-4 shrink-0 text-[#6D28D9] dark:text-[#B69BF5]" />
            <div className="text-[13px]"><b>Coming up next:</b> <b className="text-[#1B1826] dark:text-white">{nextLabel}</b> <span className="text-[#8A8593]">, {nextDesc}</span></div>
          </button>
        </div>
      </div>

      {/* HOW YOU CAN HELP AT HOME */}
      <div className="mt-4 rounded-2xl border bg-white p-6 dark:bg-white/[0.04]" style={{ borderColor: 'color-mix(in srgb, #2F8F80 34%, transparent)', backgroundImage: 'linear-gradient(180deg, rgba(47,143,128,0.09), transparent 60%)' }}>
        <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#1F6B5F] dark:text-[#5FC7B6]"><Home className="h-3.5 w-3.5" />How you can help at home</div>
        <h3 className="mt-2 font-serif text-[16px] font-semibold">Small things, five minutes each</h3>
        <p className="mb-4 mt-0.5 text-[12.5px] text-[#8A8593]">Turned from {learner.firstName}&rsquo;s exact gaps into things you can actually do, no physics degree required.</p>
        <div className="flex flex-col gap-3">
          {tips.map((t, i) => {
            const Icon = t.kind === 'reinforce' ? Compass : t.kind === 'concrete' ? Eye : Heart;
            return (
              <div key={i} className="flex gap-3.5 rounded-xl border border-black/10 bg-white/70 p-4 dark:border-white/10 dark:bg-white/[0.03]">
                <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px] bg-[#2F8F80]/16 text-[#1F6B5F] dark:text-[#5FC7B6]"><Icon className="h-4 w-4" /></span>
                <div>
                  <h5 className="text-[13.5px] font-bold">{t.title}</h5>
                  <p className="mt-1 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{t.body}</p>
                  {t.topicId && <button onClick={() => openLesson(t.topicId!)} className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]"><Play className="h-3.5 w-3.5" />Replay the lesson together</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* PROGRESS THIS TERM + EFFORT */}
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <h3 className="font-serif text-[16px] font-semibold">Progress this term</h3>
          <p className="mt-0.5 text-[12.5px] text-[#8A8593]">Is it improving? Concepts secured, week by week.</p>
          <div className="mt-4 rounded-xl border border-black/10 bg-black/[0.015] p-4 pb-1.5 dark:border-white/10 dark:bg-white/[0.02]">
            <div className="mb-1.5 flex items-center gap-2">
              <Chip soft={TONE.good.soft} ink={TONE.good.ink}><TrendingUp className="h-3 w-3" />{trendLast > trendFirst ? 'Improving steadily' : 'Holding steady'}</Chip>
              <span className="text-[12.5px] text-[#56505F] dark:text-[#B2B0C0]">{trendLast} concepts secure{trendLast > trendFirst ? `, up from ${trendFirst}` : ''}.</span>
            </div>
            <TrendLine points={trend} max={CONCEPTS.length} />
          </div>
          <p className="mb-3 mt-4 text-[12.5px] text-[#8A8593]">Tap a topic to see how it went and how to help.</p>
          <div className="flex flex-wrap gap-3">
            {attemptsByDate(learner).map((a) => {
              const t = TOPICS.find((x) => x.id === a.topicId)!;
              const tb = TONE[understandingBand(a.scorePercent).tone];
              return (
                <button key={a.topicId} onClick={() => setTopicId(a.topicId)}
                  className="min-w-[150px] flex-1 rounded-xl border border-black/10 bg-black/[0.015] p-[14px] text-left transition-colors hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-white/[0.05]">
                  <div className="flex items-center gap-1 text-[13px] font-semibold">{t.title}<ChevronRight className="h-3.5 w-3.5 text-[#8A8593]" /></div>
                  <div className="mt-1.5 font-serif text-[22px] font-semibold tabular-nums" style={{ color: tb.ink }}>{a.scorePercent}%</div>
                  <div className="mt-2"><Chip soft={tb.soft} ink={tb.ink}>{topicBandLabel(a.scorePercent)}</Chip></div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <h3 className="font-serif text-[16px] font-semibold">Effort &amp; engagement</h3>
          <p className="mt-0.5 text-[12.5px] text-[#8A8593]">Not just scores, how they are actually working.</p>
          <div className="mt-4 flex flex-col gap-3">
            <ParentInsight tone="accent" icon={Clock} title="Consistent this week" body={`Studied on ${act.days} days for ${act.minutes} minutes in total, and finished every lesson they started.`} />
            {last && last.replays > 0 && <ParentInsight tone="accent" icon={Repeat} title="Pushing on the hard parts" body={`They replayed the ${lastTopic?.title} lesson ${last.replays} times. Re-watching a tricky topic is a good sign, it is how understanding gets built.`} />}
            {act.streak > 0 && <ParentInsight tone="accent" icon={Flame} title={`${act.streak}-day streak`} body="Small and steady beats last-minute. Keeping the streak going matters more than any single score." />}
          </div>
        </div>
      </div>

      {/* TEACHER NOTE */}
      <div className="mt-4 flex items-start gap-4 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#7C3AED]/10 text-[#6D28D9] dark:text-[#B69BF5]"><BookOpen className="h-5 w-5" /></span>
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-[#8A8593]">A note from {roster.teacherName}</div>
          <p className="mt-1.5 text-[14px] text-[#56505F] dark:text-[#B2B0C0]">&ldquo;{learner.firstName} is one of my steadier students in circuits. {lastTopic?.title ?? 'The current topic'} is where most of the class is finding its feet right now, so a little reinforcement at home will go a long way. Nothing to worry about, they&rsquo;re on track.&rdquo;</p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3.5 rounded-2xl border border-black/10 bg-white p-5 text-[13px] leading-relaxed text-[#56505F] dark:border-white/10 dark:bg-white/[0.04] dark:text-[#B2B0C0]">
        <Award className="mt-0.5 h-5 w-5 shrink-0 text-[#1F6B5F] dark:text-[#5FC7B6]" />
        <div><b className="text-[#1B1826] dark:text-white">Built for young learners.</b> This report uses only {learner.firstName}&rsquo;s work inside each lesson. We store their first name and roll number, nothing else: no email, no last name, no personal details. You can ask the school to remove their data at any time. This platform uses AI to personalise practice; you and their teacher can see this progress.</div>
      </div>
    </div>
  );
}

// ---- topic drill-in ----

function ParentTopicDetail({ roster, learner, topicId, onBack, openLesson }: {
  roster: ClassRoster; learner: Learner; topicId: string; onBack: () => void; openLesson: (t: string) => void;
}) {
  const topic = TOPICS.find((t) => t.id === topicId)!;
  const a = attemptOf(learner, topicId);
  const band = a ? understandingBand(a.scorePercent) : understandingBand(0);
  const tone = TONE[band.tone];
  const traps = conceptsOfTopic(topicId)
    .map((c) => conceptMasteryOf(learner, c.tag))
    .filter((m): m is NonNullable<typeof m> => !!m && !!m.lastMisconceptionTag && m.state !== 'mastered');

  return (
    <div className="pt-2">
      <button onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]">
        <ArrowLeft className="h-4 w-4" /> Back to the report
      </button>

      <div className="flex flex-wrap items-center gap-6 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        {a && <Donut percent={a.scorePercent} color={tone.c} size={104} caption={topicBandLabel(a.scorePercent).toLowerCase()} />}
        <div className="min-w-[220px] flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#8A8593]">{learner.firstName} · {roster.name} · Physics</div>
          <h1 className="mt-1 font-serif text-[26px] font-medium tracking-tight">{topic.title}</h1>
          <p className="mt-1.5 text-[14px] text-[#56505F] dark:text-[#B2B0C0]">This topic is about {TOPIC_PLAIN[topic.id] ?? 'a new part of the syllabus.'}</p>
          {a && <p className="mt-1 text-[13.5px] text-[#56505F] dark:text-[#B2B0C0]">{learner.firstName} scored <b className="text-[#1B1826] dark:text-white">{a.scorePercent}%</b> here ({topicBandLabel(a.scorePercent).toLowerCase()}) and watched {a.watchPercent}% of the lesson.</p>}
        </div>
        <button onClick={() => openLesson(topicId)} className="inline-flex items-center gap-1.5 rounded-[10px] bg-[#2F8F80] px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#268073]"><Play className="h-4 w-4" />Watch the lesson together</button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr]">
        <div className="rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
          <h3 className="mb-4 font-serif text-[16px] font-semibold">How they did, part by part</h3>
          <div className="space-y-2.5">
            {conceptsOfTopic(topicId).map((c) => {
              const m = conceptMasteryOf(learner, c.tag);
              if (!m) return null;
              const cb = understandingBand(m.total ? (m.correct / m.total) * 100 : 0);
              const ct = TONE[cb.tone];
              return (
                <div key={c.tag} className="flex items-center justify-between gap-2 text-[13px]">
                  <span>{c.label}</span>
                  <Chip soft={ct.soft} ink={ct.ink}>{m.state === 'mastered' ? 'Secure' : m.state === 'developing' ? 'Getting there' : m.state === 'emerging' ? 'Needs work' : 'Not started'}</Chip>
                </div>
              );
            })}
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-6 dark:bg-white/[0.04]" style={{ borderColor: 'color-mix(in srgb, #2F8F80 34%, transparent)', backgroundImage: 'linear-gradient(180deg, rgba(47,143,128,0.08), transparent 60%)' }}>
          <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-wide text-[#1F6B5F] dark:text-[#5FC7B6]"><Home className="h-3.5 w-3.5" />How to help with {topic.title}</div>
          {traps.length === 0 ? (
            <p className="mt-3 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">No specific mix-up to fix here. A little encouragement, and a rewatch of the lesson if they want, is plenty.</p>
          ) : (
            <div className="mt-3 flex flex-col gap-3">
              {traps.map((m) => (
                <div key={m.conceptTag}>
                  <h5 className="text-[13.5px] font-bold">{conceptLabel(m.conceptTag)}</h5>
                  <p className="mt-1 text-[13px] text-[#56505F] dark:text-[#B2B0C0]">{plainParentOf(m.lastMisconceptionTag!)}</p>
                </div>
              ))}
              <button onClick={() => openLesson(topicId)} className="mt-1 inline-flex items-center gap-1.5 self-start text-[12.5px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]"><Play className="h-3.5 w-3.5" />Replay the {topic.title} lesson together</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- local bits ----

const plainParentOf = (tag: string) => plainMisconception(tag, 'parent');

function Chip({ children, soft, ink }: { children: React.ReactNode; soft: string; ink: string }) {
  return <span className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11.5px] font-semibold" style={{ background: soft, color: ink }}>{children}</span>;
}
function PlainPill({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-black/[0.03] px-2.5 py-1 text-[11px] font-semibold text-[#56505F] dark:border-white/10 dark:bg-white/[0.06] dark:text-[#B2B0C0]">{children}</span>;
}
function ParentInsight({ tone, title, body, icon }: { tone: 'good' | 'bad' | 'accent'; title: string; body: string; icon?: React.ComponentType<{ className?: string }> }) {
  const skin = tone === 'bad' ? 'bg-[#CB5B6B]/16 text-[#B23B4E]' : tone === 'accent' ? 'bg-[#7C3AED]/12 text-[#6D28D9] dark:text-[#B69BF5]' : 'bg-[#2F8F80]/16 text-[#1F6B5F] dark:text-[#5FC7B6]';
  const Icon = icon ?? (tone === 'bad' ? AlertTriangle : CheckCircle2);
  return (
    <div className="flex gap-3 rounded-xl border border-black/10 bg-black/[0.015] p-[14px] dark:border-white/10 dark:bg-white/[0.02]">
      <span className={`grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] ${skin}`}><Icon className="h-[18px] w-[18px]" /></span>
      <div><h5 className="mb-0.5 text-[13px] font-bold">{title}</h5><p className="text-[12.5px] text-[#56505F] dark:text-[#B2B0C0]">{body}</p></div>
    </div>
  );
}
